import { useMemo } from "react";

export const SCENARIO_COLORS = [
  { hex: "#C15A38", name: "A" },
  { hex: "#B24B3E", name: "B" },
  { hex: "#B07B2E", name: "C" },
  { hex: "#5E8467", name: "D" },
];

function coversDay(startDate, endDate, iso) {
  return startDate && iso >= startDate && iso <= (endDate || startDate);
}

export default function ScenarioCalendar({ data, month, scenarios, hiddenIds, hoveredId, onDayClick }) {
  const cells = useMemo(() => {
    if (!data) return [];
    const first = new Date(month + "-01T00:00:00");
    const lead = first.getDay(); // 0=Sun
    return [...Array(lead).fill(null), ...data.days];
  }, [data, month]);

  if (!data) {
    return <div className="border border-sand-200 p-12 text-center label-tag animate-pulse">Loading calendar…</div>;
  }

  const colorFor = (sid) => {
    const idx = scenarios.findIndex((s) => s.id === sid);
    return SCENARIO_COLORS[idx % SCENARIO_COLORS.length].hex;
  };
  const visibleScenarios = data.scenarios.filter((s) => !hiddenIds.has(s.id));
  const todayIso = new Date().toISOString().slice(0, 10);

  return (
    <div className="border-2 border-ink" data-testid="scenario-calendar">
      <div className="grid grid-cols-7 border-b-2 border-ink bg-surface">
        {["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"].map((d) => (
          <div key={d} className="px-2 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-sand-500 border-r border-sand-200 last:border-r-0">
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 bg-surface">
        {cells.map((day, i) => {
          if (!day) return <div key={`pad-${i}`} className="min-h-[104px] border-r border-b border-sand-100 bg-sand-50/60" />;
          const iso = day.date;
          const dnum = parseInt(iso.slice(8, 10), 10);
          const loc = day.location;
          const trips = data.trips.filter((t) => coversDay(t.start_date, t.end_date, iso));
          const ghosts = visibleScenarios.filter((s) => coversDay(s.start_date, s.end_date, iso));
          const isToday = iso === todayIso;
          return (
            <div key={iso}
              onClick={() => onDayClick?.(iso)}
              className={`min-h-[104px] border-r border-b border-sand-200 p-1.5 relative transition-colors hover:bg-sand-50 cursor-pointer ${isToday ? "bg-blue-50/40" : ""}`}
              title="Click to zoom into this week"
              data-testid={`calendar-day-${dnum}`}>
              <div className="flex items-start justify-between gap-1">
                <span className={`font-mono text-[11px] ${isToday ? "bg-ember text-white px-1" : "text-sand-700"}`}>{dnum}</span>
                {loc && (
                  <span
                    className={`font-mono text-[9px] uppercase tracking-wide px-1 py-0.5 truncate max-w-[64px] ${
                      loc.planned ? "text-ember border border-dashed border-ember/60" : "bg-sand-100 text-sand-600"
                    }`}
                    title={`${loc.name}${loc.airport ? ` (${loc.airport})` : ""}${loc.planned ? " — from planned flight" : ""}`}
                    data-testid={`day-location-${iso}`}>
                    {loc.airport || loc.code}{loc.planned ? "*" : ""}
                  </span>
                )}
              </div>
              <div className="mt-1 space-y-0.5">
                {trips.map((t) => {
                  const isStart = iso === t.start_date || dnum === 1 || new Date(iso + "T00:00:00").getDay() === 0;
                  return (
                    <div key={t.id}
                      className={`h-[15px] text-[8.5px] font-mono uppercase tracking-wide px-1 leading-[14px] truncate ${
                        t.status === "planned"
                          ? "text-ember border border-ember/50 planned-stripes"
                          : "bg-ember text-white"
                      }`}
                      title={`${t.name} (${t.status})`}>
                      {isStart ? t.name : "\u00A0"}
                    </div>
                  );
                })}
                {ghosts.map((s) => {
                  const c = colorFor(s.id);
                  const dim = hoveredId && hoveredId !== s.id;
                  const isStart = iso === s.start_date || dnum === 1 || new Date(iso + "T00:00:00").getDay() === 0;
                  return (
                    <div key={s.id}
                      className="h-[15px] text-[8.5px] font-mono uppercase tracking-wide px-1 leading-[12px] truncate border border-dashed bg-surface transition-opacity"
                      style={{ borderColor: c, color: c, opacity: dim ? 0.25 : hoveredId === s.id ? 1 : 0.85 }}
                      title={`Scenario: ${s.name}`}
                      data-testid={`ghost-band-${s.id}-${iso}`}>
                      {isStart ? s.name : "\u00A0"}
                    </div>
                  );
                })}
                {ghosts.filter((s) => s.days?.[iso]).length > 0 && (
                  <div className="flex flex-wrap gap-0.5 pt-0.5">
                    {ghosts.filter((s) => s.days?.[iso]).map((s) => {
                      const c = colorFor(s.id);
                      const dim = hoveredId && hoveredId !== s.id;
                      return (
                        <span key={`loc-${s.id}`}
                          className="font-mono text-[8px] uppercase tracking-wide px-0.5 border transition-opacity"
                          style={{ color: c, borderColor: c, opacity: dim ? 0.25 : 1 }}
                          title={`${s.name}: ${s.days[iso].name || s.days[iso].code}`}
                          data-testid={`scenario-loc-${s.id}-${iso}`}>
                          {s.days[iso].code}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
